import * as React from "react"
import { Link as RouterLink, useLocation } from "react-router-dom"
import { Home } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { useAuth } from "@/lib/auth"
import { useBreadcrumbs } from '@/lib/useBreadcrumbs'
import { UserMenu } from "@/components/ui/user-menu"
import { cn } from '@/lib/utils'
import { UTMLink } from '@/components/custom-ui/utm-link'
// import logoLarge from '@/assets/logo-large.png' // TODO: Add logo file

// Use UTMLink for public pages, RouterLink for authenticated pages
const Link = RouterLink

interface TopNavbarProps {
  showProfile?: boolean
  className?: string
}

export function TopNavbar({ showProfile = true, className }: TopNavbarProps) {
  const { isSignedIn } = useAuth()
  const user = isSignedIn ? { id: 'current-user' } : null
  const location = useLocation()
  const breadcrumbs = useBreadcrumbs()

  return (
    <div className={cn("flex h-16 items-center px-3 md:px-4 bg-background border-b w-full z-50 sticky top-0", className)}>
      {/* Mobile layout - centered text with logo on right */}
      {!user && (
        <>
          {/* Mobile: Centered text */}
          <div className="flex-1 md:hidden flex justify-center items-center">
            <UTMLink to="/" className="font-semibold text-base hover:text-foreground/80 transition-colors">
              RoboRelay
            </UTMLink>
          </div>

          {/* Mobile: Logo on right */}
          <div className="md:hidden">
            <UTMLink to="/" className="flex items-center">
              <div className="h-8 w-8 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-sm">RR</span>
              </div>
            </UTMLink>
          </div>
        </>
      )}

      {/* Desktop layout - left side with logo, text, and nav */}
      <div className="flex-1 min-w-0 overflow-hidden hidden md:block">
        {!user && (
          <div className="flex items-center gap-6">
            <UTMLink to="/" className="mr-8 font-semibold text-base hover:text-foreground/80 transition-colors flex items-center">
              <div className="h-8 w-8 bg-primary rounded-lg flex items-center justify-center mr-2">
                <span className="text-primary-foreground font-bold text-sm">RR</span>
              </div>
              RoboRelay
            </UTMLink>
            {/* Navigation links */}
            <div className="flex items-center gap-6">
              <UTMLink
                to="/pricing"
                className={cn(
                  "font-medium transition-colors hover:text-foreground/80",
                  location.pathname === "/pricing" ? "text-foreground" : "text-foreground/60"
                )}
              >
                Pricing
              </UTMLink>

              {/* <UTMLink
                to="/services"
                className={cn(
                  "text-xs font-medium transition-colors hover:text-foreground/80",
                  location.pathname === "/services" ? "text-foreground" : "text-foreground/60"
                )}
              >
                Services
              </UTMLink> */}
              <UTMLink
                to="/products"
                className={cn(
                  "font-medium transition-colors hover:text-foreground/80",
                  location.pathname === "/products" ? "text-foreground" : "text-foreground/60"
                )}
              >
                Product
              </UTMLink>
              <UTMLink
                to="/about"
                className={cn(
                  "font-medium transition-colors hover:text-foreground/80",
                  location.pathname === "/about" ? "text-foreground" : "text-foreground/60"
                )}
              >
                About
              </UTMLink>
              <UTMLink
                to="/blog"
                className={cn(
                  "font-medium transition-colors hover:text-foreground/80",
                  location.pathname === "/blog" ? "text-foreground" : "text-foreground/60"
                )}
              >
                Blog
              </UTMLink>
              <UTMLink
                to="/contact"
                className={cn(
                  "font-medium transition-colors hover:text-foreground/80",
                  location.pathname === "/contact" ? "text-foreground" : "text-foreground/60"
                )}
              >
                Contact
              </UTMLink>
            </div>
          </div>
        )}
      </div>

      {/* Breadcrumbs for authenticated users */}
      {user && breadcrumbs.length > 0 && (
        <div className="flex-1 min-w-0 overflow-hidden">
          <Breadcrumb>
            <BreadcrumbList className="flex items-center gap-2 min-w-0 justify-start">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  <BreadcrumbItem className="min-w-0 shrink-0">
                    {crumb.current ? (
                      <BreadcrumbPage className="flex items-center">
                        {crumb.icon ? (
                          <Home size={16} strokeWidth={2} aria-hidden="true" className="shrink-0" />
                        ) : (
                          <span className="truncate text-sm font-medium max-w-[120px] md:max-w-[200px]" title={crumb.label}>
                            {crumb.label}
                          </span>
                        )}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild>
                        <Link to={crumb.href || "#"} className="flex items-center hover:text-foreground transition-colors">
                          {crumb.icon ? (
                            <Home size={16} strokeWidth={2} aria-hidden="true" className="shrink-0" />
                          ) : (
                            <span className="truncate text-sm max-w-[120px] md:max-w-[200px]" title={crumb.label}>
                              {crumb.label}
                            </span>
                          )}
                        </Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                  {index < breadcrumbs.length - 1 && (
                    <BreadcrumbSeparator className="shrink-0 text-muted-foreground"> / </BreadcrumbSeparator>
                  )}
                </React.Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      )}

      {/* Right side - Profile */}
      <div className="flex items-center gap-2 md:gap-4 shrink-0">
        {user && showProfile ? (
          <UserMenu />
        ) : !user ? (
          <div className="flex items-center gap-2 shrink-0">
            <UTMLink to="/login" className="hidden md:block">
              <Button size="sm">Log In</Button>
            </UTMLink>
          </div>
        ) : null}
      </div>
    </div>
  )
} 